import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Sparkles } from 'lucide-react'
import { ASSISTANT_UNDER_PANEL_LAYER, ASSISTANT_WINDOW_LAYER } from '@/features/ai-chat/pageModal'
import { useOpenPanelBelow } from '@/components/ui/stacking-layer'
import { cn } from '@/lib/utils'
import { useAssistantWindowInternal } from './assistantWindowContext'
import { useIsolateWhilePageModal } from './useIsolateWhilePageModal'

const STATUS_KEY = {
  awaitingConfirmation: 'aiChat.window.status.awaitingConfirmation',
  replying: 'aiChat.window.status.replying',
  replyFailed: 'aiChat.window.status.replyFailed',
  newReply: 'aiChat.window.status.newReply',
} as const

/** 最小化后的小条：右上角、顶栏 AI 入口下方。点一下恢复；状态按优先级显示一个（待确认 > 正在回复 > 回复失败 > 有新回复）。 */
export function AssistantMinimizedBar() {
  const { t } = useTranslation('chrome')
  const { restore, attention } = useAssistantWindowInternal()
  // 页面有模态对话框时点小条不能把它关掉：与浮窗同一套事件隔离；判据按浮窗元素算（小条自己不含对话框）
  const [el, setEl] = useState<HTMLButtonElement | null>(null)
  const windowEl = useCallback(() => document.querySelector<HTMLElement>('[data-assistant-window]'), [])
  useIsolateWhilePageModal(el, windowEl)
  // 小条在右上角，正压在右侧创建面板的表单上：面板开着时同浮窗一起沉下去
  const underPanel = useOpenPanelBelow(ASSISTANT_WINDOW_LAYER)
  return (
    <button
      ref={setEl}
      type="button"
      onClick={restore}
      data-assistant-minimized-bar=""
      aria-label={t('aiChat.window.restore')}
      // Messages 侧边栏开着时让到它左边（否则压住侧边栏标题栏的关闭按钮）
      style={{ right: 'calc(1rem + var(--chat-panel-width, 0px))' }}
      className={cn(
        'pointer-events-auto fixed top-[72px] flex max-w-[280px] items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm shadow-lg transition-colors hover:bg-accent',
        underPanel ? ASSISTANT_UNDER_PANEL_LAYER : ASSISTANT_WINDOW_LAYER,
      )}
    >
      {attention === 'replying'
        ? <Loader2 className="h-4 w-4 flex-none animate-spin text-muted-foreground" />
        : <Sparkles className="h-4 w-4 flex-none text-primary" />}
      <span className="truncate font-medium text-foreground">{t('aiChat.title')}</span>
      {attention && (
        <span className={cn('truncate text-xs', attention === 'replying' ? 'text-muted-foreground' : attention === 'replyFailed' ? 'text-red-600 dark:text-red-400' : 'text-primary')}>
          {t(STATUS_KEY[attention])}
        </span>
      )}
    </button>
  )
}
